import React, { useState } from 'react';
import { applicantAuthApi } from '../../services/tradeLicenseApi';

const splitFullName = (fullName) => {
  const parts = String(fullName || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return { first_name: '', last_name: '' };
  return {
    first_name: parts[0],
    last_name: parts.slice(1).join(' '),
  };
};

const ApplicantLogin = ({ onSuccess, onCancel }) => {
  const [mode, setMode] = useState('login');
  const [step, setStep] = useState('details');
  const [phone, setPhone] = useState('');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const resetFlow = (nextMode) => {
    setMode(nextMode);
    setStep('details');
    setOtp('');
    setError('');
    setInfo('');
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setInfo('');
    try {
      await applicantAuthApi.ensureCsrf();
      let res;
      if (mode === 'login') {
        res = await applicantAuthApi.sendLoginOtp(phone);
      } else {
        const { first_name, last_name } = splitFullName(fullName);
        res = await applicantAuthApi.sendSignupOtp(phone, first_name, last_name, email);
      }
      setInfo(res.data.message || 'OTP sent to your mobile.');
      if (res.data.debug_otp) {
        setInfo(`${res.data.message} (Dev OTP: ${res.data.debug_otp})`);
      }
      setStep('otp');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send OTP.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const { first_name, last_name } = splitFullName(fullName);
      let res;
      if (mode === 'login') {
        res = await applicantAuthApi.verifyLoginOtp(phone, otp);
      } else {
        res = await applicantAuthApi.verifySignupOtp(phone, otp, {
          email,
          first_name,
          last_name,
        });
      }
      onSuccess?.(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'OTP verification failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="applicant-login-card">
      <div className="form-header">
        <h2>Applicant Portal</h2>
        <p>Login to track applications or create a new account</p>
      </div>

      <div className="auth-mode-tabs">
        <button
          type="button"
          className={`auth-mode-btn ${mode === 'login' ? 'active' : ''}`}
          onClick={() => resetFlow('login')}
        >
          Login
        </button>
        <button
          type="button"
          className={`auth-mode-btn ${mode === 'signup' ? 'active' : ''}`}
          onClick={() => resetFlow('signup')}
        >
          Sign Up
        </button>
      </div>

      {step === 'details' ? (
        <form onSubmit={handleSendOtp} className="applicant-login-form">
          <div className="form-group">
            <label>Mobile Number *</label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
              placeholder="10-digit mobile number"
              required
            />
          </div>

          {mode === 'signup' && (
            <>
              <div className="form-group">
                <label>Full Name *</label>
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="As per official documents"
                  required
                />
              </div>
              <div className="form-group">
                <label>Email (optional)</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="For notifications"
                />
              </div>
            </>
          )}

          {mode === 'login' && (
            <p className="auth-hint">
              OTP is sent only if your mobile number is registered. New users should sign up first.
            </p>
          )}

          {error && <div className="alert alert-error">{error}</div>}
          <div className="form-actions">
            {onCancel && (
              <button type="button" className="btn btn-secondary" onClick={onCancel}>
                Continue Without Login
              </button>
            )}
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Sending…' : 'Send OTP'}
            </button>
          </div>
        </form>
      ) : (
        <form onSubmit={handleVerifyOtp} className="applicant-login-form">
          <p className="otp-sent-info">{info || `OTP sent to ${phone}`}</p>
          <div className="form-group">
            <label>Enter 6-digit OTP *</label>
            <input
              type="text"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              maxLength={6}
              required
            />
          </div>
          {error && <div className="alert alert-error">{error}</div>}
          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setStep('details')}>
              Change Details
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Verifying…' : mode === 'login' ? 'Verify & Login' : 'Verify & Sign Up'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default ApplicantLogin;
